import { useState } from 'react'
import {
  type Client,
  fmtPhone,
  useForgetClientMutation,
  useMergeClientsMutation,
  useRevokeConsentMutation,
  useUpdateClientMutation,
} from '@/entities/client'
import { errorMessage } from '@/shared/api'
import { fmtFullDate } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input, Textarea } from '@/shared/ui/field'
import { Select } from '@/shared/ui/select'
import { toast } from '@/shared/ui/toast'

interface ClientDialogProps {
  client: Client | null
  /** Кандидаты на объединение — остальные контакты выборки. */
  candidates: Client[]
  onClose: () => void
}

/**
 * Карточка клиента: контакты, история, журнал согласий. Согласие — журнал, не
 * флаг: показываются обе отметки, потому что доказуемость требует именно этого.
 */
export function ClientDialog({ client, candidates, onClose }: ClientDialogProps) {
  const update = useUpdateClientMutation()
  const merge = useMergeClientsMutation()
  const forget = useForgetClientMutation()
  const revoke = useRevokeConsentMutation()

  const [name, setName] = useState(client?.name ?? '')
  const [phone, setPhone] = useState(client?.phone ?? '')
  const [note, setNote] = useState(client?.note ?? '')
  const [mergeTarget, setMergeTarget] = useState('')
  const [key, setKey] = useState('')

  if (client && key !== client.id) {
    setKey(client.id)
    setName(client.name)
    setPhone(client.phone)
    setNote(client.note)
    setMergeTarget('')
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!client) return
    update.mutate(
      { id: client.id, patch: { name: name.trim(), phone: phone.trim(), note: note.trim() } },
      { onSuccess: onClose },
    )
  }

  const error = update.error ?? merge.error ?? forget.error ?? revoke.error
  const editable = client !== null && !client.forgotten

  return (
    <Dialog
      open={client !== null}
      onClose={onClose}
      title={client?.name ?? 'Клиент'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Закрыть
          </Button>
          <Button type="submit" form="client-form" disabled={!editable || update.isPending}>
            Сохранить
          </Button>
        </>
      }
    >
      {client && (
        <div className="flex flex-col gap-6">
          {client.forgotten && (
            <p className="text-sm text-warning">
              Данные стёрты — контакт обезличен и правке не подлежит.
            </p>
          )}

          <form id="client-form" onSubmit={submit} className="flex flex-col gap-4">
            <Field label="Имя">
              {(props) => (
                <Input
                  {...props}
                  value={name}
                  disabled={!editable}
                  onChange={(e) => setName(e.target.value)}
                />
              )}
            </Field>
            <Field label="Телефон" hint="Не уникален: у мужа и жены может быть один номер">
              {(props) => (
                <Input
                  {...props}
                  type="tel"
                  value={phone}
                  disabled={!editable}
                  onChange={(e) => setPhone(e.target.value)}
                />
              )}
            </Field>
            <Field label="Заметка">
              {(props) => (
                <Textarea
                  {...props}
                  value={note}
                  disabled={!editable}
                  onChange={(e) => setNote(e.target.value)}
                />
              )}
            </Field>
          </form>

          <section className="flex flex-col gap-2">
            <h3 className="label">История</h3>
            {client.visits && client.visits.length > 0 ? (
              <ul className="flex flex-col gap-1">
                {client.visits.map((visit) => (
                  <li key={visit.excursionId} className="flex justify-between gap-3 text-sm">
                    <span className="truncate text-text-primary">{visit.excursionTitle}</span>
                    <span className="mono-num shrink-0 text-text-tertiary">
                      {fmtFullDate(new Date(visit.heldAt))}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-text-tertiary">Ещё не ходил.</p>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="label">Согласия</h3>
            <ul className="flex flex-col gap-2">
              {client.consents.map((consent) => (
                <li key={consent.id} className="flex flex-col gap-1 text-sm">
                  <span className="flex items-center gap-2">
                    <Chip tone={consent.revokedAt ? 'neutral' : 'success'}>
                      {consent.revokedAt ? 'отозвано' : 'действует'}
                    </Chip>
                    <span className="mono-num text-xs text-text-tertiary">
                      {fmtFullDate(new Date(consent.grantedAt))}
                      {consent.revokedAt && ` → ${fmtFullDate(new Date(consent.revokedAt))}`}
                    </span>
                    <span className="text-xs text-text-tertiary">
                      {consent.source === 'public-form' ? 'через форму' : 'со слов'}
                    </span>
                  </span>
                  <span className="text-xs text-text-tertiary">{consent.wording}</span>
                </li>
              ))}
              {client.consents.length === 0 && (
                <li className="text-sm text-text-tertiary">Записей нет.</li>
              )}
            </ul>
            {editable && client.consents.some((c) => c.revokedAt === null) && (
              <Button
                variant="secondary"
                size="sm"
                className="self-start"
                disabled={revoke.isPending}
                onClick={() =>
                  revoke.mutate(client.id, {
                    onSuccess: (result) =>
                      toast.notify(
                        result.hasActiveBookings
                          ? 'Согласие отозвано, но действующие записи остались — разберите вручную'
                          : 'Согласие отозвано',
                      ),
                  })
                }
              >
                Отозвать согласие
              </Button>
            )}
          </section>

          {editable && (
            <section className="flex flex-col gap-2">
              <h3 className="label">Дубль</h3>
              <p className="text-xs text-text-tertiary">
                Объединение всегда вручную. Записи остаются на дубле, а он указывает на основной
                контакт — поэтому решение обратимо.
              </p>
              <div className="flex flex-wrap items-end gap-2">
                <Select
                  aria-label="Основной контакт"
                  value={mergeTarget}
                  onChange={setMergeTarget}
                  placeholder="Выбрать основной контакт"
                  className="max-w-xs"
                  options={candidates
                    .filter((c) => c.id !== client.id && !c.forgotten && c.mergedInto === null)
                    .map((c) => ({ value: c.id, label: `${c.name} · ${fmtPhone(c.phone)}` }))}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!mergeTarget || merge.isPending}
                  onClick={() =>
                    merge.mutate(
                      { sourceId: client.id, targetId: mergeTarget },
                      { onSuccess: onClose },
                    )
                  }
                >
                  Объединить
                </Button>
              </div>
            </section>
          )}

          {editable && (
            <Button
              variant="danger"
              size="sm"
              className="self-start"
              disabled={forget.isPending}
              onClick={() => forget.mutate(client.id, { onSuccess: onClose })}
            >
              Забыть клиента
            </Button>
          )}

          {error && (
            <p role="alert" className="text-sm text-accent">
              {errorMessage(error)}
            </p>
          )}
        </div>
      )}
    </Dialog>
  )
}
